import { Component } from '@theme/component';

/**
 * Countdown savings component. Ticks a Days/Hrs/Mins/Secs countdown toward a
 * merchant-configured end date and shows an expired state once it elapses.
 *
 * Implemented as a custom element (rather than an inline <script>) because the
 * Theme Editor's Section Rendering API replaces block markup via morph/innerHTML,
 * which never re-executes inline scripts. Custom elements upgrade automatically
 * whenever they're inserted into the DOM, so this keeps working after block edits.
 *
 * @typedef {object} CountdownSavingsRefs
 * @property {HTMLElement} timer
 * @property {HTMLElement} expiredMessage
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<CountdownSavingsRefs>}
 */
export class CountdownSavingsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const endValue = this.dataset.countdownEnd;
    this.endDate = endValue ? new Date(endValue) : this.#getEvergreenEndDate();
    this.hideWhenExpired = this.dataset.countdownHideWhenExpired === 'true';

    this.tick();
    this.intervalId = window.setInterval(() => this.tick(), 1000);
  }

  /**
   * Falls back to a rolling countdown when no explicit end date is configured.
   * The target is cached in localStorage per block so a visitor sees a
   * consistent, decreasing countdown across page views instead of it
   * resetting to the full duration on every reload.
   *
   * @returns {Date}
   */
  #getEvergreenEndDate() {
    const hours = Number(this.dataset.countdownEvergreenHours) || 48;
    const durationMs = hours * 60 * 60 * 1000;
    const storageKey = `countdown-savings:${this.dataset.countdownKey ?? ''}`;

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const storedDate = new Date(stored);
        if (!Number.isNaN(storedDate.getTime()) && storedDate.getTime() > Date.now()) {
          return storedDate;
        }
      }
    } catch {
      // localStorage may be unavailable (e.g. private browsing); fall through to a fresh countdown.
    }

    const target = new Date(Date.now() + durationMs);

    try {
      window.localStorage.setItem(storageKey, target.toISOString());
    } catch {
      // Ignore storage failures; the countdown still works for this page view.
    }

    return target;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.intervalId);
  }

  tick() {
    const { timer, expiredMessage, days, hours, minutes, seconds } = this.refs;

    if (!this.endDate || Number.isNaN(this.endDate.getTime())) {
      if (timer) timer.hidden = true;
      return;
    }

    const remaining = this.endDate.getTime() - Date.now();

    if (remaining <= 0) {
      this.#setDigits(days, hours, minutes, seconds, 0, 0, 0, 0);
      this.#showExpiredState(timer, expiredMessage);
      window.clearInterval(this.intervalId);
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);

    this.#setDigits(
      days,
      hours,
      minutes,
      seconds,
      Math.floor(totalSeconds / 86400),
      Math.floor((totalSeconds % 86400) / 3600),
      Math.floor((totalSeconds % 3600) / 60),
      totalSeconds % 60
    );
  }

  /**
   * @param {HTMLElement} [daysEl]
   * @param {HTMLElement} [hoursEl]
   * @param {HTMLElement} [minutesEl]
   * @param {HTMLElement} [secondsEl]
   * @param {number} d
   * @param {number} h
   * @param {number} m
   * @param {number} s
   */
  #setDigits(daysEl, hoursEl, minutesEl, secondsEl, d, h, m, s) {
    if (daysEl) daysEl.textContent = String(d).padStart(2, '0');
    if (hoursEl) hoursEl.textContent = String(h).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(m).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(s).padStart(2, '0');
  }

  /**
   * @param {HTMLElement} [timer]
   * @param {HTMLElement} [expiredMessage]
   */
  #showExpiredState(timer, expiredMessage) {
    if (this.hideWhenExpired) {
      this.hidden = true;
      return;
    }
    if (timer) timer.hidden = true;
    if (expiredMessage) expiredMessage.hidden = false;
  }
}

if (!customElements.get('countdown-savings-component')) {
  customElements.define('countdown-savings-component', CountdownSavingsComponent);
}
